import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EMPTY_FUNCTION,
  FLOATING_ELEMENT_PLACEMENT,
} from '../../../constants/common'
import { useCopyValue, useFloatingSelect } from '../../../hooks'
import Button, { BUTTON_HEIGHT_24, BUTTON_HEIGHT_32 } from '../../Button'
import { Divider } from '../../Divider'
import { FloatingSelectDropdown } from '../../Floating'
import { SelectDropdown } from '../../Select'
import {
  QA_NAME_GRETA_AI_CHAT_CHAT_MENU,
  QA_NAME_GRETA_AI_CHAT_CHAT_MENU_COPY_ID,
  QA_NAME_GRETA_AI_CHAT_CHAT_MENU_DELETE,
  QA_NAME_GRETA_AI_CHAT_CHAT_MENU_RENAME,
} from '../GretaAIChat.constants'

import * as styles from './GretaAIChatChatMenu.module.css'

import CopyIcon from '../../../icons/copy.svg'
import DeleteIcon from '../../../icons/delete.svg'
import EditIcon from '../../../icons/edit.svg'
import MoreHorizontalIcon from '../../../icons/more-horizontal.svg'

function GretaAIChatChatMenu({
  buttonHeight = BUTTON_HEIGHT_24,
  chat,
  className,
  qaName = QA_NAME_GRETA_AI_CHAT_CHAT_MENU,
  onDelete = EMPTY_FUNCTION,
  onOpenChange = EMPTY_FUNCTION,
  onRename = EMPTY_FUNCTION,
}) {
  const { t } = useTranslation()

  const { handleCopy } = useCopyValue({
    errorNotificationKey: 'Greta.ChatIdCopyErrorKey',
    successNotificationKey: 'Greta.ChatIdCopiedKey',
    value: chat.id,
  })

  const { context, floatingStyles, getFloatingProps, isOpen, refs, setOpen } =
    useFloatingSelect({
      placement: FLOATING_ELEMENT_PLACEMENT.bottom,
      onClose: () => onOpenChange(false),
    })

  const { setReference } = refs

  const isOpenRef = useRef(isOpen)

  useEffect(() => {
    isOpenRef.current = isOpen
  }, [isOpen])

  useEffect(
    () => () => {
      if (isOpenRef.current) {
        onOpenChange(false)
      }
    },
    []
  )

  function handleToggle() {
    const nextOpen = !isOpen

    setOpen(nextOpen)
    onOpenChange(nextOpen)
  }

  function handleRename() {
    setOpen(false)
    onOpenChange(false)
    onRename(chat)
  }

  function handleCopyId() {
    setOpen(false)
    onOpenChange(false)
    handleCopy()
  }

  function handleDelete() {
    setOpen(false)
    onOpenChange(false)
    onDelete(chat)
  }

  return (
    <>
      <div ref={setReference}>
        <Button.Icon
          isGrey
          Icon={MoreHorizontalIcon}
          beforeIconProps={{ height: BUTTON_HEIGHT_24 }}
          className={className}
          height={buttonHeight}
          qaName={qaName}
          onClick={handleToggle}
        />
      </div>
      <FloatingSelectDropdown
        context={context}
        floatingStyles={floatingStyles}
        getFloatingProps={getFloatingProps}
        isOpen={isOpen}
        refs={refs}
      >
        <SelectDropdown>
          <div className={styles.menu}>
            <Button.Flat
              isGrey
              beforeIconProps={{
                Icon: EditIcon,
                iconProps: { className: styles.itemIcon },
              }}
              className={styles.item}
              height={BUTTON_HEIGHT_32}
              qaName={QA_NAME_GRETA_AI_CHAT_CHAT_MENU_RENAME}
              text={t('Greta.RenameChatActionKey')}
              textClassName={styles.itemLabel}
              onClick={handleRename}
            />
            <Divider />
            <Button.Flat
              isGrey
              beforeIconProps={{
                Icon: CopyIcon,
                iconProps: { className: styles.itemIcon },
              }}
              className={styles.item}
              height={BUTTON_HEIGHT_32}
              qaName={QA_NAME_GRETA_AI_CHAT_CHAT_MENU_COPY_ID}
              text={t('Greta.CopyChatIdKey')}
              textClassName={styles.itemLabel}
              onClick={handleCopyId}
            />
            <Divider />
            <Button.Flat
              isGrey
              beforeIconProps={{
                Icon: DeleteIcon,
                iconProps: { className: styles.itemIcon },
              }}
              className={styles.item}
              height={BUTTON_HEIGHT_32}
              qaName={QA_NAME_GRETA_AI_CHAT_CHAT_MENU_DELETE}
              text={t('Greta.DeleteChatActionKey')}
              textClassName={styles.itemLabel}
              onClick={handleDelete}
            />
          </div>
        </SelectDropdown>
      </FloatingSelectDropdown>
    </>
  )
}

export default GretaAIChatChatMenu
